import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {exportSessions} from 'benchling-apps/interactivity/sessions/SessionDAO';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ORANGE} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';

import {BenchlingAppActivitySessionsQuery} from './__generated/BenchlingAppActivitySessionsQuery';

type AccessibleApp = ExtractByTypename<BenchlingAppActivitySessionsQuery['benchlingApp'], 'BenchlingAppNode'>;

type BenchlingAppSessionNode = AccessibleApp['sessionListing']['sessions'][number];

export const APP_STATUS_EXPORT_LIMIT = 100000;

export interface ExportSessionActivityDropdownProps {
  benchlingApp: AccessibleApp;
  modifiedAtFilter: {
    fromDatetime: string | null;
    toDatetime: string | null;
  };
  sessionNameFilter: string | null;
  sessionStatusFilter: Array<string>;
  filteredSessions: Array<BenchlingAppSessionNode>;
}

export default function ExportSessionActivityDropdown(
  props: ExportSessionActivityDropdownProps
): JSX.Element {
  return (
    <Dropdown
      menu={
        <Menu width={Menu.WIDTHS.LARGE} height={Menu.HEIGHTS.MEDIUM}>
          <Option
            onSelect={() => {
              runExportSessionActivityModal({
                appId: props.benchlingApp.id,
                modifiedAtFilter: {fromDatetime: null, toDatetime: null},
                sessionNameFilter: null,
                sessionStatusFilter: [],
              });
            }}
          >
            Export all activity &nbsp;&nbsp;
            {props.benchlingApp.sessionListing.total <= APP_STATUS_EXPORT_LIMIT ? (
              <></>
            ) : (
              <BenchlingIcon icon="warning" className={css(styles.warningIcon)} />
            )}
          </Option>
          <Option
            onSelect={() => {
              runExportSessionActivityModal({
                appId: props.benchlingApp.id,
                modifiedAtFilter: props.modifiedAtFilter,
                sessionNameFilter: props.sessionNameFilter,
                sessionStatusFilter: props.sessionStatusFilter,
              });
            }}
          >
            Export filtered activity (
            {props.filteredSessions.length <= APP_STATUS_EXPORT_LIMIT
              ? props.filteredSessions.length
              : APP_STATUS_EXPORT_LIMIT}
            ) &nbsp;&nbsp;
            {props.filteredSessions.length <= APP_STATUS_EXPORT_LIMIT ? (
              <></>
            ) : (
              <BenchlingIcon icon="warning" className={css(styles.warningIcon)} />
            )}
          </Option>
        </Menu>
      }
    >
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.SMALL}
        icon="ellipsis"
        isDropdown={true}
        hideDropdownCaret={true}
      />
    </Dropdown>
  );
}

export interface ExportSessionActivityModalProps {
  onRequestSubmit: (format: string) => void;
  onRequestClose: () => void;
}

export function ExportSessionActivityModal(props: ExportSessionActivityModalProps): JSX.Element {
  const [selectedFormat, setSelectedFormat] = React.useState<string>();

  return (
    <Modal
      enableRedesign={true}
      title="Export activity"
      onRequestClose={props.onRequestClose}
      medium={true}
      onRequestSubmit={() => {
        props.onRequestSubmit(selectedFormat!);
      }}
      buttons={[
        {
          text: 'Export',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: selectedFormat == null,
        },
      ]}
      dataTestAttrs={{component: 'ExportSessionActivityModal', element: 'root'}}
    >
      <div className="form-group">
        <SelectDropdown
          displayName={selectedFormat ?? 'Select export format'}
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
              <Option onSelect={setSelectedFormat} value="CSV">
                CSV
              </Option>
              <Option onSelect={setSelectedFormat} value="PDF">
                PDF
              </Option>
            </Menu>
          }
          dataTestAttrs={{component: 'ExportSessionActivityModal', element: 'dropdown'}}
        />
      </div>
    </Modal>
  );
}

export interface RunExportSessionActivityModalProps {
  appId: string;
  modifiedAtFilter: {
    fromDatetime: string | null;
    toDatetime: string | null;
  };
  sessionNameFilter: string | null;
  sessionStatusFilter: Array<string>;
}

export function runExportSessionActivityModal(props: RunExportSessionActivityModalProps): void {
  runModal(ExportSessionActivityModal, {
    onRequestSubmit: (format: string) => {
      const notificationId = `export-session-activity-${Date.now()}`;
      notifications.info('Exporting...', {id: notificationId, duration: 0});
      exportSessions({
        appId: props.appId,
        exportFormat: format,
        startTime:
          props.modifiedAtFilter.fromDatetime != null ? props.modifiedAtFilter.fromDatetime : undefined,
        endTime: props.modifiedAtFilter.toDatetime != null ? props.modifiedAtFilter.toDatetime : undefined,
        sessionName: props.sessionNameFilter != null ? props.sessionNameFilter : undefined,
        sessionStatuses: props.sessionStatusFilter.length !== 0 ? props.sessionStatusFilter : undefined,
      })
        .done(({url}: {url: string}) => {
          window.location.href = url;
          notifications.success('Export complete. Downloading...', {id: notificationId});
        })
        .fail((response: Response) => {
          notifications.notifyError(response, {id: notificationId});
        });
    },
  });
}

export const styles = StyleSheet.create({
  warningIcon: {
    marginRight: '3px',
    color: ORANGE,
  },
});
